import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Check } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import type { Plan } from '../types/subscription-types';
import { analyticsService } from '@/shared/services/analytics';
import { PlanService } from '../services/PlanService';
import { getLocalizedText } from '@/shared/utils/i18nHelper';

interface PlanSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan: (plan: Plan) => void;
  selectedPlanId?: string;
}

export const PlanSelector = ({ isOpen, onClose, onSelectPlan, selectedPlanId }: PlanSelectorProps) => {
  const { i18n } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentLang = i18n.language.split('-')[0];

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await PlanService.getPlans({ is_active: true });
      if (response.success) {
        // Ordenar por sort_order
        const sortedPlans = response.data.sort((a, b) => a.sort_order - b.sort_order);
        setPlans(sortedPlans);
      }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar planos');
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (plan: Plan) => {
    const price = currentLang === 'pt' ? plan.content.price_brl : plan.content.price_usd;
    const currency = currentLang === 'pt' ? 'R$' : '$';
    
    if (price === 0) {
      return currentLang === 'pt' ? 'Grátis' : 'Free';
    }
    
    return `${currency} ${price.toFixed(2)}`;
  };

  const getBillingCycleLabel = (cycle: string) => {
    const labels: Record<string, Record<string, string>> = {
      monthly: { pt: '/mês', en: '/month' },
      yearly: { pt: '/ano', en: '/year' },
      lifetime: { pt: 'vitalício', en: 'lifetime' },
    };
    return labels[cycle]?.[currentLang] || cycle;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      {/* Flyout */}
      <div className="relative h-full w-full max-w-2xl bg-background shadow-2xl animate-slide-in-right">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div>
              <h2 className="text-xl font-semibold">
                {currentLang === 'pt' ? 'Escolha seu Plano' : 'Choose Your Plan'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {currentLang === 'pt' ? 'Selecione o plano ideal para sua organização' : 'Select the ideal plan for your organization'}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1 px-6 py-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : error ? (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center">
                <p className="text-sm text-destructive">{error}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={loadPlans}>
                  {currentLang === 'pt' ? 'Tentar Novamente' : 'Try Again'}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
                {plans.map((plan) => {
                  const isSelected = plan.id === selectedPlanId;
                  const isPopular = plan.content.metadata?.popular === true;

                  return (
                    <div
                      key={plan.id}
                      className={`relative rounded-lg border-2 p-4 transition-all flex flex-col ${
                        isSelected
                          ? 'border-primary bg-primary/5 shadow-lg'
                          : 'border-border hover:border-primary/50 hover:shadow-md'
                      }`}
                    >
                      {isPopular && (
                        <Badge className="absolute -top-3 left-3 bg-primary text-xs">
                          {plan.content.metadata?.badge ? getLocalizedText(plan.content.metadata.badge, currentLang) : (currentLang === 'pt' ? 'Popular' : 'Popular')}
                        </Badge>
                      )}

                      <div className="space-y-3 flex-1">
                        <div>
                          <h3 className="text-base font-semibold">{plan.name}</h3>
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                            {getLocalizedText(plan.content.description, currentLang)}
                          </p>
                        </div>
                          
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold">{formatPrice(plan)}</span>
                          <span className="text-xs text-muted-foreground">
                            {getBillingCycleLabel(plan.content.billing_cycle)}
                          </span>
                        </div>

                        {plan.content.trial_days && (
                          <Badge variant="outline" className="text-xs">
                            {plan.content.trial_days} {currentLang === 'pt' ? 'dias grátis' : 'free days'}
                          </Badge>
                        )}

                        {/* Features - Compact */}
                        <div>
                          <h4 className="text-xs font-medium mb-2 text-muted-foreground">
                            {currentLang === 'pt' ? 'Recursos inclusos:' : 'Included features:'}
                          </h4>
                          <ul className="space-y-1">
                            {plan.content.features.slice(0, 10).map((feature, index) => (
                              <li key={index} className="flex items-start gap-1.5 text-xs">
                                <Check className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                <span>{getLocalizedText(feature, currentLang)}</span>
                              </li>
                            ))}
                            {plan.content.features.length > 10 && (
                              <li className="text-xs text-muted-foreground pl-4 pt-1">
                                +{plan.content.features.length - 10} {currentLang === 'pt' ? 'recursos inclusos' : 'included features'}
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>

                      <Button
                        onClick={() => {
                          // Track plan selection
                          analyticsService.trackEvent('plan_selected', {
                            plan_name: plan.name,
                            plan_id: plan.id,
                            billing_cycle: plan.content.billing_cycle,
                            price: currentLang === 'pt' ? plan.content.price_brl : plan.content.price_usd
                          });
                          
                          onSelectPlan(plan);
                          onClose();
                        }}
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        className="w-full mt-4"
                      >
                        {isSelected ? (
                          <>
                            <Check className="mr-2 h-3 w-3" />
                            {currentLang === 'pt' ? 'Selecionado' : 'Selected'}
                          </>
                        ) : (
                          plan.content.metadata?.cta_text 
                            ? getLocalizedText(plan.content.metadata.cta_text, currentLang)
                            : (currentLang === 'pt' ? 'Escolher' : 'Choose')
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};
